import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'

const METRIC_LABELS = {
  van_sales: 'Van Sales',
  revenue: 'Revenue',
  gross_margin: 'Gross Margin',
  net_margin: 'Net Margin',
  total_sales: 'Total Sales',
  total_cogs: 'Total COGS',
  net_income: 'Net Income',
  marketing_spend: 'Marketing Spend',
  website_visits: 'Website Visits',
  instagram_followers: 'Instagram Followers',
  inbound_leads: 'Inbound Leads',
  youtube_video_views: 'YouTube Views',
  net_assets: 'Net Assets'
}

const PERIOD_OPTIONS = [
  { value: 4, label: '1 Month' },
  { value: 12, label: '3 Months' },
  { value: 26, label: '6 Months' },
  { value: 52, label: '1 Year' },
  { value: 104, label: '2 Years' },
  { value: 156, label: '3 Years' }
]

const GROUP_BY_OPTIONS = [
  { value: 1, label: '1 Week' },
  { value: 4, label: '1 Month' },
  { value: 13, label: '1 Quarter' },
  { value: 52, label: '1 Year' }
]

export default function MetricDetail() {
  const { metricType } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [period, setPeriod] = useState(52)
  const [groupBy, setGroupBy] = useState(4) // Default to 1 month

  // Update period when metricType changes
  useEffect(() => {
    setPeriod(metricType === 'website_visits' ? 104 : 52)
  }, [metricType])

  useEffect(() => {
    if (!metricType) return

    setLoading(true)
    // Include group_by parameter for metrics that support grouping
    const supportsGrouping = ['van_sales', 'total_sales', 'total_cogs', 'net_income', 'marketing_spend', 'inbound_leads'].includes(metricType)
    const url = supportsGrouping
      ? `/api/metrics/${metricType}?period=${period}&group_by=${groupBy}`
      : `/api/metrics/${metricType}?period=${period}`

    fetch(url)
      .then(response => response.json())
      .then(data => {
        setData(data)
        setLoading(false)
      })
      .catch(err => {
        console.error('Error fetching metric data:', err)
        setLoading(false)
      })
  }, [metricType, period, groupBy])

  const handlePointClick = (event) => {
    if (metricType === 'van_sales') {
      // Get the clicked date (this is the END of the period)
      const clickedDate = new Date(event.point.x)

      // Calculate the start date (groupBy weeks before the clicked date)
      const startDate = new Date(clickedDate)
      startDate.setDate(startDate.getDate() - (groupBy * 7))

      // Format dates as YYYY-MM-DD
      const formatDate = (date) => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }

      // Navigate to van sales page with date range
      navigate(`/van-sales?startDate=${formatDate(startDate)}&endDate=${formatDate(clickedDate)}`)
    }
  }

  const chartOptions = {
    chart: {
      type: 'line',
      height: 500
    },
    title: {
      text: METRIC_LABELS[metricType] || metricType
    },
    xAxis: {
      type: 'datetime',
      title: {
        text: 'Date'
      }
    },
    yAxis: {
      title: {
        text: 'Value'
      }
    },
    plotOptions: metricType === 'van_sales' ? {
      series: {
        cursor: 'pointer',
        point: {
          events: {
            click: handlePointClick
          }
        }
      }
    } : {},
    series: [{
      name: METRIC_LABELS[metricType] || metricType,
      data: data.map(item => [new Date(item.date).getTime(), parseFloat(item.value)]),
      color: '#1f2937'
    }],
    tooltip: {
      enabled: true,
      ...(['van_sales', 'total_sales', 'total_cogs', 'net_income', 'marketing_spend', 'inbound_leads'].includes(metricType) ? {
        pointFormatter: function() {
          const periodLabel = GROUP_BY_OPTIONS.find(opt => opt.value === groupBy)?.label || `${groupBy} weeks`
          const metricLabel = METRIC_LABELS[metricType] || metricType

          // Format currency values (sales, cogs, net income, marketing spend)
          const formattedValue = ['total_sales', 'total_cogs', 'net_income', 'marketing_spend'].includes(metricType)
            ? `$${(this.y / 1000).toFixed(1)}k`
            : this.y

          return `<span style="color:${this.color}">●</span> ${metricLabel} in prior ${periodLabel}: <b>${formattedValue}</b><br/>`
        }
      } : {})
    },
    credits: {
      enabled: false
    },
    legend: {
      enabled: false
    }
  }

  return (
    <div className="px-4 sm:px-0">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <Link
            to="/"
            className="text-sm text-gray-600 hover:text-gray-900 mb-2 inline-block"
          >
            ← Back to Dashboard
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">
            {METRIC_LABELS[metricType] || metricType}
          </h1>
        </div>

        <div className="flex gap-4">
          {['van_sales', 'total_sales', 'total_cogs', 'net_income', 'marketing_spend', 'inbound_leads'].includes(metricType) && (
            <div>
              <label htmlFor="groupBy" className="mr-2 text-sm font-medium text-gray-700">
                {metricType === 'van_sales' ? 'Group Sales By:' : 'Group By:'}
              </label>
              <select
                id="groupBy"
                value={groupBy}
                onChange={(e) => setGroupBy(parseInt(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                {GROUP_BY_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label htmlFor="period" className="mr-2 text-sm font-medium text-gray-700">
              Time Period:
            </label>
            <select
              id="period"
              value={period}
              onChange={(e) => setPeriod(parseInt(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            >
              {PERIOD_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        {loading ? (
          <div className="flex justify-center items-center h-96">
            <div className="text-gray-500">Loading chart...</div>
          </div>
        ) : (
          <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
          />
        )}
      </div>
    </div>
  )
}
